import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Badge,
  Button,
  Callout,
  CodeBlock,
  Icon,
  Input,
  Modal,
  Stack,
  Textarea,
  icons,
  theme,
} from '@agentisai/design-system';
import { EVAL_SET_NAME, EvalSetSchema } from '@agentisai/shared-types';
import { ApiError } from '../../queries/client';
import { useDeleteEvalSet, useEvalDocuments, useSaveEvalSet } from '../../queries/useEvalFixtures';
import { CaseEditor } from './CaseEditor';
import { muted } from './format';
import {
  draftIssues,
  duplicateCase,
  emptyCase,
  fromDraft,
  type CaseDraft,
  type SetDraft,
} from './setDraft';

/** `?case=` — the open case (by position), URL-owned (R3) so it survives a reload. */
const CASE_PARAM = 'case';

function saveErrorText(err: unknown, creating: boolean): string {
  if (err instanceof ApiError && err.status === 409) {
    return creating
      ? 'A set with that name already exists. Pick another name.'
      : 'Someone saved this set after you opened it. Copy your changes, reload the page and apply them again.';
  }
  if (err instanceof ApiError && err.status === 422) {
    return `The server rejected the set: ${err.message}`;
  }
  return "Couldn't save the set. Try again.";
}

/**
 * A question set as a list of cases beside the one being edited — never a wall
 * of forms. The working copy is local state (R1.0); `key={etag}` on the caller
 * resets it after a save, and "unsaved" is derived by comparing the saved shape.
 */
export function SetEditor({
  initial,
  etag,
  canWrite,
  onSaved,
  onClose,
}: {
  initial: SetDraft;
  etag: string | null;
  canWrite: boolean;
  onSaved: (name: string) => void;
  onClose: () => void;
}) {
  const creating = etag === null;
  const [draft, setDraft] = useState(initial);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  // The saved shape never changes for this editor's life (`key={etag}` remounts
  // it after a save), so it is serialised once, not on every keystroke.
  const [savedShape] = useState(() => JSON.stringify(fromDraft(initial)));
  const [searchParams, setSearchParams] = useSearchParams();
  const documents = useEvalDocuments(true);
  const save = useSaveEvalSet();
  const remove = useDeleteEvalSet();
  const docs = documents.data?.data.documents ?? [];

  const candidate = fromDraft(draft);
  const parsed = EvalSetSchema.safeParse(candidate);
  const issues = draftIssues(draft, parsed.success ? [] : parsed.error.issues);
  const issueCount = [...issues.values()].reduce((n, list) => n + list.length, 0);
  const dirty = JSON.stringify(candidate) !== savedShape;
  const canSave = canWrite && parsed.success && issueCount === 0 && dirty && !save.isPending;

  const raw = Number(searchParams.get(CASE_PARAM));
  const selected = Number.isInteger(raw) && raw >= 0 && raw < draft.cases.length ? raw : 0;
  const select = (i: number) =>
    setSearchParams(
      (prev) => {
        prev.set(CASE_PARAM, String(i));
        return prev;
      },
      { replace: true },
    );

  // External-system sync (R24): warn before a reload/close drops unsaved edits.
  useEffect(() => {
    if (!dirty || !canWrite) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, canWrite]);

  const setCases = (cases: CaseDraft[], open: number) => {
    setDraft({ ...draft, cases });
    select(Math.max(0, Math.min(open, cases.length - 1)));
  };
  const current = draft.cases[selected];
  const setIssues = issues.get(-1) ?? [];

  return (
    <Stack
      as="section"
      aria-label={creating ? 'New question set' : `Set ${initial.name}`}
      direction="column"
      gap={4}
    >
      <Stack direction="row" gap={2} align="center" justify="between" wrap>
        <h2 style={{ margin: 0, fontSize: theme.typography.fontSize.titleLg }}>
          {creating ? 'New question set' : initial.name}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => (dirty && canWrite ? setConfirmLeave(true) : onClose())}
        >
          Back to sets
        </Button>
      </Stack>

      <fieldset disabled={!canWrite} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
        <Stack direction={{ sm: 'column', md: 'row' }} gap={3}>
          {creating && (
            <Input
              label="Set name"
              helper="e.g. claims-policy. Can't be changed later."
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              error={
                draft.name && !EVAL_SET_NAME.test(draft.name)
                  ? 'Lowercase letters, numbers and dashes'
                  : undefined
              }
            />
          )}
          <Input
            label="Set tags"
            helper="Comma-separated; every case carries them."
            value={draft.tags}
            onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
          />
          <Textarea
            label="What this set is for"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            autoResize
            maxRows={3}
            fullWidth
          />
        </Stack>
      </fieldset>

      <Stack direction={{ sm: 'column', md: 'row' }} gap={5} align="start">
        <Stack
          as="nav"
          aria-label="Cases"
          direction="column"
          gap={1}
          style={{
            flex: `0 0 ${theme.size.railPanelWidth}px`,
            minWidth: 0,
            width: '100%',
            maxWidth: theme.size.railPanelWidth,
          }}
        >
          <span style={muted}>
            {draft.cases.length} case{draft.cases.length === 1 ? '' : 's'}
          </span>
          {draft.cases.map((c, i) => {
            const n = issues.get(i)?.length ?? 0;
            return (
              <Button
                key={i}
                variant={i === selected ? 'secondary' : 'ghost'}
                size="sm"
                fullWidth
                aria-current={i === selected ? 'true' : undefined}
                aria-label={`Case ${i + 1}: ${c.name || 'Untitled'}${n ? ` (${n} to fix)` : ''}`}
                onClick={() => select(i)}
                rightIcon={
                  n > 0 ? (
                    <Badge tone="warn" size="sm">
                      {n}
                    </Badge>
                  ) : c.documents.length > 0 ? (
                    <Icon as={icons.Paperclip} size="sm" />
                  ) : undefined
                }
              >
                <span
                  style={{
                    flex: 1,
                    textAlign: 'start',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {i + 1}. {c.name || 'Untitled case'}
                </span>
              </Button>
            );
          })}
          {canWrite && (
            <Stack direction="row" gap={1} wrap style={{ marginTop: theme.space[2] }}>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Icon as={icons.Plus} size="sm" />}
                onClick={() =>
                  setCases([...draft.cases, emptyCase(draft.cases.length)], draft.cases.length)
                }
              >
                Add case
              </Button>
              {current && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Icon as={icons.Copy} size="sm" />}
                  onClick={() =>
                    setCases(
                      [
                        ...draft.cases.slice(0, selected + 1),
                        duplicateCase(
                          current,
                          draft.cases.map((c) => c.id),
                        ),
                        ...draft.cases.slice(selected + 1),
                      ],
                      selected + 1,
                    )
                  }
                >
                  Duplicate
                </Button>
              )}
              {current && draft.cases.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Icon as={icons.Trash2} size="sm" />}
                  aria-label={`Remove case ${selected + 1}`}
                  onClick={() =>
                    setCases(
                      draft.cases.filter((_, j) => j !== selected),
                      selected - 1,
                    )
                  }
                >
                  Remove
                </Button>
              )}
            </Stack>
          )}
        </Stack>

        <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
          {current && (
            <CaseEditor
              key={selected}
              draft={current}
              index={selected}
              documents={docs}
              canWrite={canWrite}
              issues={issues.get(selected) ?? []}
              onChange={(next) =>
                setDraft({
                  ...draft,
                  cases: draft.cases.map((c, j) => (j === selected ? next : c)),
                })
              }
            />
          )}
        </div>
      </Stack>

      {!creating && (
        <details>
          <summary style={{ cursor: 'pointer', ...muted }}>Run this set</summary>
          <Stack direction="column" gap={1} style={{ marginTop: theme.space[2] }}>
            <span style={muted}>From apps/workstreamagent/api (repeat to see the spread):</span>
            <CodeBlock
              code={`uv run python evals/run.py --sync --set ${initial.name} --repeat 3 --publish`}
              language="bash"
            />
          </Stack>
        </details>
      )}

      {save.isError && (
        <div role="alert">
          <Callout tone="danger">{saveErrorText(save.error, creating)}</Callout>
        </div>
      )}
      {remove.isError && (
        <div role="alert">
          <Callout tone="danger">Couldn't delete the set.</Callout>
        </div>
      )}

      {canWrite && (
        <Stack
          direction="row"
          gap={3}
          align="center"
          justify="between"
          wrap
          style={{
            position: 'sticky',
            bottom: 0,
            background: theme.color.neutral.bgSurface,
            borderTop: `1px solid ${theme.color.neutral.border}`,
            paddingBlock: theme.space[3],
          }}
        >
          <span style={muted} aria-live="polite">
            {setIssues.length > 0
              ? setIssues.join('; ')
              : issueCount > 0
                ? `${issueCount} thing${issueCount === 1 ? '' : 's'} to fix before saving (see the flagged cases)`
                : dirty
                  ? 'Unsaved changes'
                  : 'All changes saved'}
          </span>
          <Stack direction="row" gap={2}>
            {!creating && (
              <Button variant="softRed" size="sm" onClick={() => setConfirmDelete(true)}>
                Delete set
              </Button>
            )}
            <Button
              variant="primary"
              disabled={!canSave}
              loading={save.isPending}
              onClick={() =>
                parsed.success &&
                save.mutate(
                  { set: parsed.data, etag },
                  { onSuccess: (r) => onSaved(r.data.set.name) },
                )
              }
            >
              {creating ? 'Create set' : 'Save changes'}
            </Button>
          </Stack>
        </Stack>
      )}

      {confirmLeave && (
        <Modal
          open
          onClose={() => setConfirmLeave(false)}
          title="Discard unsaved changes?"
          size="sm"
          actions={
            <>
              <Button variant="ghost" onClick={() => setConfirmLeave(false)}>
                Keep editing
              </Button>
              <Button variant="destructive" onClick={onClose}>
                Discard
              </Button>
            </>
          }
        >
          Your edits to this set haven't been saved.
        </Modal>
      )}
      {confirmDelete && (
        <Modal
          open
          onClose={() => setConfirmDelete(false)}
          title={`Delete ${initial.name}?`}
          size="sm"
          actions={
            <>
              <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                loading={remove.isPending}
                disabled={remove.isPending}
                onClick={() => remove.mutate(initial.name, { onSuccess: onClose })}
              >
                Delete
              </Button>
            </>
          }
        >
          Published runs of this set stay on the Runs tab. The bucket keeps the previous version, so
          the set can be restored.
        </Modal>
      )}
    </Stack>
  );
}
SetEditor.displayName = 'SetEditor';
